import { useEffect, useState } from "react";

import { PspRules } from "@CommonTypes/backend/PspRules";
import { Flex, Grid, NumberInput, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { DEFAULT_HEAVY_ITEM_THRESHOLD_G } from "src/pages/Merchflows_merchflowId_BasePog_basePogId_/store/types";

import { RulesSection } from "./components/RulesSection";
import { RuleRange } from "./components/RuleRange";
import { RuleToggle } from "./components/RuleToggle";

type Rules = NonNullable<PspRules["weight_rules"]>;

interface Props {
  isDisabled?: boolean;

  /** Grams. Null means shelves without their own rating have no cap at all. */
  defaultShelfWeightLimit: Rules["default_shelf_weight_limit"];
  setDefaultShelfWeightLimit: (value: Rules["default_shelf_weight_limit"]) => void;

  draftShelfCapGrams: number;
  setDraftShelfCapGrams: (value: number) => void;

  shelfCapOn: boolean;
  setShelfCapOn: (value: boolean) => void;

  /** Null is the whole heavy rule off; present, it carries both numbers. */
  heavyRule: Rules["heavy_rule"];
  setHeavyRule: (value: Rules["heavy_rule"]) => void;

  resolveOverweightShelves: Rules["resolve_overweight_shelves"];
  setResolveOverweightShelves: (value: Rules["resolve_overweight_shelves"]) => void;

  swapHeavyBrandsBelowMaxHeight: Rules["swap_heavy_brands_below_max_height"];
  setSwapHeavyBrandsBelowMaxHeight: (value: Rules["swap_heavy_brands_below_max_height"]) => void;

  swapFillFloorPct: Rules["swap_fill_floor_pct"];
  setSwapFillFloorPct: (value: Rules["swap_fill_floor_pct"]) => void;
}

const GRAMS_PER_POUND = 453.59237;

const asPounds = (grams: number) => (grams / GRAMS_PER_POUND).toFixed(1);

export const WeightRules = ({
  isDisabled,
  defaultShelfWeightLimit,
  setDefaultShelfWeightLimit,
  draftShelfCapGrams,
  setDraftShelfCapGrams,
  shelfCapOn,
  setShelfCapOn,
  heavyRule,
  setHeavyRule,
  resolveOverweightShelves,
  setResolveOverweightShelves,
  swapHeavyBrandsBelowMaxHeight,
  setSwapHeavyBrandsBelowMaxHeight,
  swapFillFloorPct,
  setSwapFillFloorPct,
}: Props) => {
  const { t } = useTranslation("common");

  // Local, because `heavyRule` is null-or-complete on the wire and so cannot
  // express "section on, max_deck_y not entered yet". It stays null until the
  // user supplies one: a guessed height line in the wrong geometry scale is
  // what put 63 of 76 shelves over the line on a real merchant.
  const [sectionOn, setSectionOn] = useState(heavyRule !== null);
  const [draftThreshold, setDraftThreshold] = useState(
    heavyRule?.item_threshold ?? DEFAULT_HEAVY_ITEM_THRESHOLD_G,
  );
  const [draftMaxDeckY, setDraftMaxDeckY] = useState<number | null>(heavyRule?.max_deck_y ?? null);

  useEffect(() => {
    setSectionOn(heavyRule !== null);
    setDraftThreshold(heavyRule?.item_threshold ?? DEFAULT_HEAVY_ITEM_THRESHOLD_G);
    setDraftMaxDeckY(heavyRule?.max_deck_y ?? null);
  }, [heavyRule]);

  const heavyDisabled = isDisabled || !sectionOn;

  const shelfCapDisabled = isDisabled || !shelfCapOn;

  return (
    <Grid
      areas={`
        "shelf-cap    heavy-rule"
        "resolve      swap"
        "advanced     advanced"
      `}
      gap="20px"
      padding="20px"
    >
      {/* Switching off clears the cap, not the draft: re-enabling restores the
          last entered value instead of forcing re-entry. */}
      <RulesSection
        title={t("planogramRules.weightRules.shelfCap.title")}
        area="shelf-cap"
        toggleValue={shelfCapOn}
        setToggleValue={(enabled) => {
          setShelfCapOn(enabled);
          setDefaultShelfWeightLimit(enabled ? draftShelfCapGrams || null : null);
        }}
        isDisabled={isDisabled}
      >
        <Flex column padding="10px" gap="10px">
          <Text variant="small1" isDisabled={shelfCapDisabled}>
            {t("planogramRules.weightRules.shelfCap.limit")}
          </Text>

          <NumberInput
            title={t("planogramRules.weightRules.shelfCap.limit")}
            value={draftShelfCapGrams}
            // Emptied becomes null, not 0: the contract rejects 0 at `ge=1`.
            setValue={(value) => {
              setDraftShelfCapGrams(value);
              setDefaultShelfWeightLimit(value > 0 ? value : null);
            }}
            min={0}
            isRequired={shelfCapOn}
            // An enabled cap without grams serializes as null, so Generate must also check the enabled state.

            error={
              shelfCapOn && draftShelfCapGrams <= 0
                ? t("planogramRules.weightRules.shelfCap.limitRequired")
                : false
            }
            isDisabled={shelfCapDisabled}
          />

          <Text variant="caption1" isDisabled={shelfCapDisabled}>
            {defaultShelfWeightLimit
              ? t("planogramRules.weightRules.shelfCap.pounds", {
                  pounds: asPounds(defaultShelfWeightLimit),
                })
              : t("planogramRules.weightRules.shelfCap.noCap")}
          </Text>
        </Flex>
      </RulesSection>

      {/* Switching off clears the draft too, so re-opening starts clean. */}
      <RulesSection
        title={t("planogramRules.weightRules.heavyRule.title")}
        area="heavy-rule"
        toggleValue={sectionOn}
        setToggleValue={(enabled) => {
          setSectionOn(enabled);
          if (!enabled) {
            setDraftMaxDeckY(null);
            setHeavyRule(null);
          }
        }}
        isDisabled={isDisabled}
      >
        <Flex column padding="10px" gap="10px">
          <Text variant="small1" isDisabled={heavyDisabled}>
            {t("planogramRules.weightRules.heavyRule.threshold")}
          </Text>

          <NumberInput
            title={t("planogramRules.weightRules.heavyRule.threshold")}
            value={draftThreshold}
            setValue={(value) => {
              const itemThreshold = Math.max(1, value);
              setDraftThreshold(itemThreshold);
              if (draftMaxDeckY !== null)
                setHeavyRule({ item_threshold: itemThreshold, max_deck_y: draftMaxDeckY });
            }}
            min={1}
            isDisabled={heavyDisabled}
          />

          <Text variant="caption1" isDisabled={heavyDisabled}>
            {t("planogramRules.weightRules.heavyRule.thresholdPounds", {
              pounds: asPounds(draftThreshold),
            })}
          </Text>

          <Text variant="small1" isDisabled={heavyDisabled}>
            {t("planogramRules.weightRules.heavyRule.maxDeckY")}
          </Text>

          <NumberInput
            title={t("planogramRules.weightRules.heavyRule.maxDeckY")}
            value={draftMaxDeckY ?? 0}
            // 0/empty means "not entered", which keeps the rule off the wire.
            setValue={(value) => {
              const maxDeckY = value > 0 ? value : null;
              setDraftMaxDeckY(maxDeckY);
              setHeavyRule(
                maxDeckY === null ? null : { item_threshold: draftThreshold, max_deck_y: maxDeckY },
              );
            }}
            min={0}
            isRequired
            error={
              sectionOn && draftMaxDeckY === null
                ? t("planogramRules.weightRules.heavyRule.maxDeckYRequired")
                : false
            }
            isDisabled={heavyDisabled}
          />

          {/*
            Names no unit, deliberately: the value is compared against `shelf_y`
            raw, so suggesting millimetres against a tenths-of-a-mm payload
            would flag nearly every shelf.
          */}
          <Text variant="caption1" isDisabled={heavyDisabled}>
            {t("planogramRules.weightRules.heavyRule.maxDeckYHelp")}
          </Text>
        </Flex>
      </RulesSection>

      <RulesSection
        title={t("planogramRules.weightRules.passes.resolveTitle")}
        area="resolve"
        isDisabled={isDisabled}
      >
        <RuleToggle
          title={t("planogramRules.weightRules.passes.resolve")}
          value={resolveOverweightShelves}
          setValue={setResolveOverweightShelves}
          isDisabled={isDisabled}
        />
        <Flex padding="0 10px 10px">
          <Text variant="caption1" isDisabled={isDisabled}>
            {t("planogramRules.weightRules.passes.resolveHelp")}
          </Text>
        </Flex>
      </RulesSection>

      <RulesSection
        title={t("planogramRules.weightRules.passes.swapTitle")}
        area="swap"
        isDisabled={isDisabled}
      >
        <RuleToggle
          title={t("planogramRules.weightRules.passes.swap")}
          value={swapHeavyBrandsBelowMaxHeight}
          setValue={setSwapHeavyBrandsBelowMaxHeight}
          isDisabled={isDisabled}
        />
        <Flex padding="0 10px 10px">
          {/* Pogger treats this switch without a heavy rule as inert, not an
              error — so explain rather than block the toggle. */}
          <Text variant="caption1" isDisabled={isDisabled}>
            {heavyRule === null
              ? t("planogramRules.weightRules.passes.swapNeedsHeavyRule")
              : t("planogramRules.weightRules.passes.swapHelp")}
          </Text>
        </Flex>
      </RulesSection>

      <RulesSection
        title={t("planogramRules.weightRules.advanced.title")}
        area="advanced"
        isDisabled={isDisabled}
      >
        {/* A fraction in [0,1] despite the `_pct` wire name. Presented as a
            percent, 90 would clamp to 1 and refuse every swap. */}
        <RuleRange
          title={t("planogramRules.weightRules.advanced.fillFloor")}
          min={0}
          max={1}
          step={0.01}
          value={swapFillFloorPct}
          setValue={setSwapFillFloorPct}
          leftLabel="0"
          rightLabel="1"
          isDisabled={isDisabled}
        />
      </RulesSection>
    </Grid>
  );
};
